/*
 * <rent-vs-own> — rent or own payback calculator.
 * No dependencies. The purchase price comes from the product (data-price, in dollars);
 * everything else is the visitor's own numbers, which never leave the browser.
 * Maths ported from the earlier build's rentVsOwn.ts, rounding rule unchanged:
 * payback lands on a whole rental, and months are derived from that same rounded count.
 */
(function () {
  if (customElements.get('rent-vs-own')) return;

  /** Accepts "220", "1,200", "$220.50"; returns null for blank, NaN for garbage. */
  function parseAmount(raw) {
    var cleaned = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
    if (cleaned === '') return null;
    if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '.') return NaN;
    return Number(cleaned);
  }

  function compute(inputs) {
    var rental = parseAmount(inputs.rental);
    var bookings = parseAmount(inputs.bookings);
    var delivery = parseAmount(inputs.delivery);
    var price = parseAmount(inputs.price);

    var values = [rental, bookings, delivery, price];
    for (var i = 0; i < values.length; i++) {
      if (values[i] !== null && isNaN(values[i])) return { state: 'invalid' };
    }
    if (rental === null || bookings === null) return { state: 'empty' };

    var perBooking = rental + (delivery === null ? 0 : delivery);
    var annual = perBooking * bookings;
    var frequent = bookings >= 3;

    if (perBooking <= 0 || bookings <= 0) return { state: 'empty' };

    if (price === null || price <= 0) {
      return { state: 'annual', perBooking: perBooking, bookings: bookings, annual: annual, frequent: frequent };
    }

    var rentalsToPayback = Math.ceil(price / perBooking);
    return {
      state: 'payback',
      perBooking: perBooking,
      bookings: bookings,
      annual: annual,
      frequent: frequent,
      rentalsToPayback: rentalsToPayback,
      monthsToPayback: (rentalsToPayback / bookings) * 12
    };
  }

  function formatDollars(n) {
    try {
      return n.toLocaleString('en-CA', {
        style: 'currency',
        currency: 'CAD',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0
      });
    } catch (e) {
      return '$' + Math.round(n).toLocaleString('en-CA');
    }
  }

  function formatMonths(n) {
    if (n < 10) return (Math.round(n * 10) / 10).toLocaleString('en-CA');
    return Math.round(n).toLocaleString('en-CA');
  }

  /** Stepper nudge: from blank, the first press lands on one step. Never below 0. */
  function nudge(raw, delta) {
    var current = parseAmount(raw);
    var base = current === null || isNaN(current) ? 0 : current;
    var next = Math.max(0, Math.round((base + delta) * 100) / 100);
    return String(next);
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  class RentVsOwn extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.price = this.dataset.price || '';
      this.fields = {};
      this.querySelectorAll('[data-field]').forEach(
        function (input) {
          this.fields[input.dataset.field] = input;
        }.bind(this)
      );
      this.outRentals = this.querySelector('[data-out="rentals"]');
      this.outMonths = this.querySelector('[data-out="months"]');
      this.sentence = this.querySelector('[data-sentence]');
      this.readout = this.querySelector('[data-readout]');

      var stringsEl = this.querySelector('script[data-strings]');
      try {
        this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};
      } catch (e) {
        this.strings = {};
      }

      this.addEventListener('input', this.update.bind(this));
      this.addEventListener('keydown', this.onKey.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.update();
    }

    onKey(event) {
      var input = event.target;
      if (!input.dataset || !input.dataset.field) return;
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault();
      var step = Number(input.dataset.step) || 1;
      input.value = nudge(input.value, event.key === 'ArrowUp' ? step : -step);
      this.update();
    }

    onClick(event) {
      var button = event.target.closest('[data-nudge]');
      if (!button || !this.contains(button)) return;
      var input = this.fields[button.dataset.nudge];
      if (!input) return;
      var step = Number(input.dataset.step) || 1;
      input.value = nudge(input.value, button.dataset.direction === 'down' ? -step : step);
      this.update();
    }

    setNumber(el, value) {
      if (!el) return;
      var slot = el.querySelector('[data-value]');
      var empty = el.querySelector('[data-empty]');
      if (value === null) {
        el.setAttribute('data-state', 'empty');
        if (slot) slot.textContent = '';
        if (empty) empty.hidden = false;
        return;
      }
      el.setAttribute('data-state', 'filled');
      if (empty) empty.hidden = true;
      if (slot && slot.textContent !== value) {
        slot.textContent = value;
        slot.classList.remove('is-settling');
        void slot.offsetWidth;
        slot.classList.add('is-settling');
      }
    }

    update() {
      var s = this.strings;
      var result = compute({
        rental: this.fields.rental ? this.fields.rental.value : '',
        bookings: this.fields.bookings ? this.fields.bookings.value : '',
        delivery: this.fields.delivery ? this.fields.delivery.value : '',
        price: this.price
      });

      Object.keys(this.fields).forEach(
        function (key) {
          var value = parseAmount(this.fields[key].value);
          var bad = value !== null && isNaN(value);
          if (bad) this.fields[key].setAttribute('aria-invalid', 'true');
          else this.fields[key].removeAttribute('aria-invalid');
        }.bind(this)
      );

      if (this.readout) this.readout.setAttribute('data-state', result.state);

      var html = '';
      if (result.state === 'empty') {
        this.setNumber(this.outRentals, null);
        this.setNumber(this.outMonths, null);
        html = escapeHtml(s.empty);
      } else if (result.state === 'invalid') {
        this.setNumber(this.outRentals, null);
        this.setNumber(this.outMonths, null);
        html = escapeHtml(s.invalid);
      } else {
        var annual = '<strong>' + escapeHtml(fill(s.per_year, { amount: formatDollars(result.annual) })) + '</strong>';
        html = fill(escapeHtml(result.frequent ? s.frequent : s.infrequent), { annual: annual });

        if (result.state === 'payback') {
          var rentals = result.rentalsToPayback.toLocaleString('en-CA');
          var months = formatMonths(result.monthsToPayback);
          this.setNumber(this.outRentals, rentals);
          this.setNumber(this.outMonths, months);
          var rentalsText =
            '<strong>' +
            escapeHtml(fill(result.rentalsToPayback === 1 ? s.rental_one : s.rental_other, { count: rentals })) +
            '</strong>';
          var monthsText = '<strong>' + escapeHtml(fill(s.months, { count: months })) + '</strong>';
          html +=
            ' ' +
            fill(escapeHtml(s.payback), {
              price: escapeHtml(formatDollars(Number(parseAmount(this.price)))),
              rentals: rentalsText,
              months: monthsText
            });
        } else {
          this.setNumber(this.outRentals, null);
          this.setNumber(this.outMonths, null);
        }
      }

      if (this.sentence && this.sentence.innerHTML !== html) this.sentence.innerHTML = html;
    }
  }

  customElements.define('rent-vs-own', RentVsOwn);
})();
